// @flow
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import {Visibility} from '@material-ui/icons';
import {makeStyles} from '@material-ui/core/styles';
import {colors} from '../../../../../assets/styles/colors';
import CustomChip from '../../../../../elements/CustomChip';
import DangerIcon from '../../../../../elements/icons/DangerIcon';
import {getStringDate} from '../../../../../utils/date';
import type {
  CustomerComplaint,
  CustomerComplaintState,
} from '../../../../../entities/customer-complaints';

type Props = {|
  complaints: CustomerComplaint[],
  onViewDetail: (complaint: CustomerComplaint) => void,
  onUpdateStatus: (
    complaintId: string,
    newStatus: CustomerComplaintState
  ) => void,
  isComplaintOverdue: (complaint: CustomerComplaint) => boolean,
|};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  card: {
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  overdueCard: {
    backgroundColor: '#fff5f5',
    borderLeft: `4px solid ${colors.error}`,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  correlativeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  correlative: {
    fontWeight: 600,
    fontSize: '1rem',
    color: colors.grey900,
  },
  overdueIcon: {
    color: colors.error,
    fontSize: '1.2rem',
  },
  customerName: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: colors.grey900,
    marginBottom: theme.spacing(0.5),
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  label: {
    fontSize: '0.75rem',
    color: colors.grey600,
    fontWeight: 500,
  },
  value: {
    fontSize: '0.75rem',
    color: colors.grey800,
    fontWeight: 400,
  },
  amount: {
    fontWeight: 600,
    color: colors.grey900,
  },
  statusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  actionButton: {
    padding: '4px 12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '16px',
    cursor: 'pointer',
    border: 'none',
    '&:hover': {
      opacity: 0.8,
    },
  },
  solveButton: {
    backgroundColor: colors.grey200,
    color: colors.success,
    '&:hover': {
      backgroundColor: colors.grey300,
    },
  },
  receivedButton: {
    backgroundColor: colors.grey200,
    color: colors.danger,
    '&:hover': {
      backgroundColor: colors.grey300,
    },
  },
  viewButton: {
    color: colors.primary,
  },
}));

const getStatusColor = (status: CustomerComplaintState): string => {
  switch (status) {
    case 'RECEIVED':
      return 'warning';
    case 'SOLVED':
      return 'success';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: CustomerComplaintState): string => {
  switch (status) {
    case 'RECEIVED':
      return 'Recibido';
    case 'SOLVED':
      return 'Resuelto';
    default:
      return status;
  }
};

export default function ComplaintsMobileCards(props: Props) {
  const {complaints, onViewDetail, onUpdateStatus, isComplaintOverdue} = props;
  const classes = useStyles();

  const handleStatusChange = (complaint: CustomerComplaint) => {
    const newStatus = complaint.state === 'RECEIVED' ? 'SOLVED' : 'RECEIVED';
    onUpdateStatus(complaint.id, newStatus);
  };

  return (
    <div className={classes.container}>
      {complaints.map(complaint => {
        const isOverdue = isComplaintOverdue(complaint);

        return (
          <Card
            key={complaint.id}
            className={`${classes.card} ${
              isOverdue ? classes.overdueCard : ''
            }`}
          >
            <CardContent>
              <div className={classes.correlativeContainer}>
                {isOverdue && (
                  <Tooltip title="Reclamo vencido - Más de 10 días">
                    <DangerIcon className={classes.overdueIcon} />
                  </Tooltip>
                )}
                <Typography className={classes.correlative}>
                  {complaint.id}
                </Typography>
              </div>
              <div className={classes.cardHeader}>
                <div className={classes.correlativeContainer}>
                  <Typography className={classes.correlative}>
                    {'#'}
                    {String(complaint.externalId)?.padStart(6, '0')}
                  </Typography>
                </div>
                <IconButton
                  className={classes.viewButton}
                  onClick={() => onViewDetail(complaint)}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </div>

              <Typography className={classes.customerName}>
                {complaint.email}
              </Typography>

              <div className={classes.infoRow}>
                <span className={classes.label}>Fecha:</span>
                <span className={classes.value}>
                  {getStringDate(complaint.createdAt)}
                </span>
              </div>

              <div className={classes.infoRow}>
                <span className={classes.label}>Email:</span>
                <span className={classes.value}>{complaint.email}</span>
              </div>

              <div className={classes.infoRow}>
                <span className={classes.label}>Teléfono:</span>
                <span className={classes.value}>{complaint.phone}</span>
              </div>

              <div className={classes.statusRow}>
                <CustomChip
                  label={getStatusLabel(complaint.state)}
                  color={getStatusColor(complaint.state)}
                  size="small"
                />
                <button
                  type="button"
                  className={`${classes.actionButton} ${
                    complaint.state === 'RECEIVED'
                      ? classes.solveButton
                      : classes.receivedButton
                  }`}
                  onClick={() => handleStatusChange(complaint)}
                >
                  {complaint.state === 'RECEIVED'
                    ? 'Marcar Resuelto'
                    : 'Revertir a Recibido'}
                </button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
